import { useEffect, useState } from "react";
import StressProfileManager from "components/experiment/StressProfileManager";
import { emptyStep, stepError } from "components/experiment/StressProfileEditor";
import StartRunConfirmModal from "components/experiment/StartRunConfirmModal";
import { sumStepDurations } from "utils/stressProfileFormat";
import { normalizeWaterEvents, validateWaterEvents } from "utils/waterEvents";
import {
  WATER_SAMPLE_SECONDS_MIN,
  WATER_SAMPLE_SECONDS_MAX,
  WATER_DISTANCE_SAMPLES_MIN,
  WATER_DISTANCE_SAMPLES_MAX,
  WATER_DISTANCE_SAMPLE_WORST_MS,
} from "utils/constants";

const RUN_ID = /^[A-Za-z0-9_-]{1,64}$/;
// User's own stated experiment preset (see backend/firmware CHANGELOGs,
// 2026-09-25): 10s interval, 50 samples/reading - a noise floor closer to
// production's 200-sample average than the lab firmware's 1s/20 default.
const DEFAULT_SAMPLE_SECONDS = "10";
const DEFAULT_DISTANCE_SAMPLES = "50";
// Tank limits the water events are checked against. The bench tank is 45 cm tall; 40 leaves a
// margin below the rim. The starting level has no default on purpose: it must be read off the ruler.
const DEFAULT_TANK_MIN_CM = "0";
const DEFAULT_TANK_MAX_CM = "40";

// A run ID nobody has to think of: "stress-20261020-1430" (editable under Advanced).
export const defaultRunId = (now = new Date()) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `stress-${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`;
};

// A preset's water events as the editor rows hold them (strings while typing).
const editorEvents = (events) =>
  (events || []).map((e) => ({ at_minute: String(e.at_minute), action: e.action, cm: String(e.cm) }));

export default function GreenhouseRunForm({
  activeController,
  disabled,
  onStart,
  onStop,
  profiles,
  onCreateProfile,
  onArchiveProfile,
  waterDeviceMac,
  pumpConfig,
  // From the Experiment designer: the preset picked in its gallery ({key, name, steps, water_events, profileId}),
  // loaded into the editor whenever a different one is picked, and a callback told about every plan change (for
  // the designer's preview chart).
  preset = null,
  onPlanChange = null,
}) {
  const [runId, setRunId] = useState(() => defaultRunId());
  const [label, setLabel] = useState("");
  const [steps, setSteps] = useState([emptyStep()]);
  const [profileId, setProfileId] = useState("");
  const [waterEvents, setWaterEvents] = useState([]);
  const [startLevelCm, setStartLevelCm] = useState("");
  const [tankMinCm, setTankMinCm] = useState(DEFAULT_TANK_MIN_CM);
  const [tankMaxCm, setTankMaxCm] = useState(DEFAULT_TANK_MAX_CM);
  const [linkedPadihatRunId, setLinkedPadihatRunId] = useState("");
  const [sampleSeconds, setSampleSeconds] = useState(DEFAULT_SAMPLE_SECONDS);
  const [distanceSamples, setDistanceSamples] = useState(DEFAULT_DISTANCE_SAMPLES);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  // Non-null while the confirm-before-start modal is open, holding the
  // already-validated payload it'll pass to onStart on "Confirm & start".
  const [confirmPayload, setConfirmPayload] = useState(null);

  const activeRun = activeController?.active_run;

  const presetKey = preset ? `${preset.key}:${preset.nonce ?? ""}` : "";
  useEffect(() => {
    if (!preset) return;
    setSteps((preset.steps || []).map((step) => ({ ...step })));
    setWaterEvents(editorEvents(preset.water_events));
    setLabel(preset.name || "");
    setProfileId(preset.profileId || "");
    // Only when a different preset is picked, never on a re-render of the same one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetKey]);

  useEffect(() => {
    if (onPlanChange) onPlanChange({ steps, waterEvents, startLevelCm });
  }, [onPlanChange, steps, waterEvents, startLevelCm]);

  // Live check of the water events against the run length and the tank setup, same rules as the
  // backend (which stays the authority). Shown row by row while editing and blocks Start.
  const waterValidation = validateWaterEvents(waterEvents, {
    config: pumpConfig || undefined,
    totalRunSeconds: sumStepDurations(steps),
    startLevelCm,
    tankMinCm,
    tankMaxCm,
  });

  // Soft, advisory-only warning (matches firmware's own serial warning and
  // the backend's lack of a hard block) - the requested cadence will just
  // silently run slower than asked, never blocks submit.
  const cadenceWarning = (() => {
    if (!waterDeviceMac) return null;
    const seconds = Number(sampleSeconds);
    const count = Number(distanceSamples);
    if (!Number.isFinite(seconds) || !Number.isFinite(count)) return null;
    const worstCaseMs = count * WATER_DISTANCE_SAMPLE_WORST_MS;
    if (worstCaseMs <= seconds * 1000) return null;
    return `${count} samples can take up to ~${(worstCaseMs / 1000).toFixed(1)}s worst-case, longer than the ${seconds}s interval — actual cadence may run slower than requested.`;
  })();

  // Real bug found 2026-09-15: the banner below used to always show
  // whatever `message` was set to right after submit - "Command sent -
  // waiting for the controller to acknowledge" - and nothing ever
  // refreshed it, so it stayed on screen forever even once the real ack
  // arrived a second later. This derives live status from the polled
  // activeController data instead, so it reflects the truth on every
  // refresh rather than freezing at submit time.
  const ackLine = (() => {
    const cmd = activeRun?.latest_command;
    if (!cmd) return null;
    const verb = cmd.command === "stop_run" ? "Stop" : "Start";
    if (!cmd.acknowledged) return `${verb} command sent — waiting for the controller to acknowledge…`;
    if (cmd.ack_status && cmd.ack_status !== "ok") {
      return `${verb} command rejected by the controller${cmd.ack_reason ? `: ${cmd.ack_reason}` : "."}`;
    }
    return `${verb} command acknowledged by the controller ✓`;
  })();

  // Validation stays exactly here, run BEFORE the confirm modal opens - the
  // modal is a review step, not a second validation pass. On success it no
  // longer calls onStart directly; it opens StartRunConfirmModal with the
  // validated payload, and the modal's own "Confirm & start" button is what
  // actually calls onStart (see confirmStart below).
  const submitStart = (event) => {
    event.preventDefault();
    setMessage("");
    if (!RUN_ID.test(runId)) {
      setMessage("Run ID: use 1–64 letters, numbers, hyphens, or underscores.");
      return;
    }
    if (!steps.length) {
      setMessage("Add at least one step.");
      return;
    }
    const firstError = steps.map(stepError).find(Boolean);
    if (firstError) {
      setMessage(firstError);
      return;
    }
    if (waterValidation.firstError) {
      setMessage(waterValidation.firstError);
      return;
    }
    // Sampling fields only apply (and are only shown) when a water device
    // is linked - see the `waterDeviceMac &&` guard around their <label>s.
    let sampleMs = null;
    let distanceSamplesInt = null;
    if (waterDeviceMac) {
      const seconds = Number(sampleSeconds);
      if (!Number.isFinite(seconds) || seconds < WATER_SAMPLE_SECONDS_MIN || seconds > WATER_SAMPLE_SECONDS_MAX) {
        setMessage(`Sample interval: enter ${WATER_SAMPLE_SECONDS_MIN}–${WATER_SAMPLE_SECONDS_MAX} seconds.`);
        return;
      }
      const count = Number(distanceSamples);
      if (
        !Number.isInteger(count) ||
        count < WATER_DISTANCE_SAMPLES_MIN ||
        count > WATER_DISTANCE_SAMPLES_MAX
      ) {
        setMessage(`Samples per reading: enter a whole number ${WATER_DISTANCE_SAMPLES_MIN}–${WATER_DISTANCE_SAMPLES_MAX}.`);
        return;
      }
      sampleMs = Math.round(seconds * 1000);
      distanceSamplesInt = count;
    }
    setConfirmPayload({
      runId,
      label,
      steps,
      profileId: profileId || null,
      linkedPadihatRunId,
      waterEvents: normalizeWaterEvents(waterEvents),
      // Only meaningful (and only validated) when there are events.
      waterStartLevelCm: waterEvents.length ? Number(startLevelCm) : null,
      tankMinCm: waterEvents.length ? Number(tankMinCm) : null,
      tankMaxCm: waterEvents.length ? Number(tankMaxCm) : null,
      // Always sent (not only when changed from the default) when a water
      // device is linked, so a previous run's config can never silently
      // carry over into this one.
      sampleMs,
      distanceSamples: distanceSamplesInt,
    });
  };

  const confirmStart = async () => {
    if (!confirmPayload) return;
    setSubmitting(true);
    try {
      const result = await onStart(confirmPayload);
      setMessage(
        result.status === "sent"
          ? "Command sent — waiting for the controller to acknowledge."
          : "Saved, but the MQTT publish failed. The controller has not received this run."
      );
      setConfirmPayload(null);
    } catch (error) {
      setMessage(error.message);
      setConfirmPayload(null);
    } finally {
      setSubmitting(false);
    }
  };

  const submitStop = async () => {
    setMessage("");
    setSubmitting(true);
    try {
      await onStop();
      setMessage("Stop command sent — the controller will return to its safe resting state.");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      {activeRun ? (
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-emerald-600">Run active</p>
              <p className="mt-1 text-sm font-semibold text-slate-800">
                {activeRun.run_id}
                {activeRun.profile_name ? ` (${activeRun.profile_name})` : ""}
              </p>
              <p className="text-xs text-slate-500">
                Started {new Date(activeRun.started_at).toLocaleString()}
                {activeRun.linked_padihat_run_id ? ` — linked to ${activeRun.linked_padihat_run_id}` : ""}
              </p>
            </div>
            <button
              onClick={submitStop}
              disabled={disabled || submitting}
              className="rounded-xl border border-red-200 bg-white px-4 py-2.5 text-sm font-bold text-red-700 disabled:opacity-50"
            >
              {submitting ? "Stopping…" : "Stop run"}
            </button>
          </div>
          {ackLine && (
            <p
              role="status"
              className={`text-xs ${
                activeRun.latest_command?.acknowledged
                  ? activeRun.latest_command.ack_status === "ok"
                    ? "text-emerald-700"
                    : "text-red-700"
                  : "text-slate-500"
              }`}
            >
              {ackLine}
            </p>
          )}
        </div>
      ) : (
        <form onSubmit={submitStart} className="flex flex-col gap-3">
          <details className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2" data-testid="stress-advanced">
            <summary className="cursor-pointer text-xs font-bold uppercase tracking-wide text-slate-500">
              Advanced: run name, linked run, sampling
            </summary>
            <div className="mt-2 flex flex-col gap-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <label className="text-sm font-semibold text-slate-700">
              Run ID
              <input
                value={runId}
                onChange={(event) => setRunId(event.target.value)}
                placeholder="temp-stress-001"
                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 font-mono text-sm"
              />
            </label>
            <label className="text-sm font-semibold text-slate-700">
              Label (optional)
              <input
                value={label}
                onChange={(event) => setLabel(event.target.value)}
                placeholder="Hot & humid hold"
                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              />
            </label>
            <label className="text-sm font-semibold text-slate-700">
              Linked water-level run ID (optional)
              <input
                value={linkedPadihatRunId}
                onChange={(event) => setLinkedPadihatRunId(event.target.value)}
                placeholder="water-level-001"
                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 font-mono text-sm"
              />
            </label>
          </div>
          {waterDeviceMac && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <label className="text-sm font-semibold text-slate-700">
                Sample interval (s)
                <input
                  type="number"
                  min={WATER_SAMPLE_SECONDS_MIN}
                  max={WATER_SAMPLE_SECONDS_MAX}
                  step="0.25"
                  value={sampleSeconds}
                  onChange={(event) => setSampleSeconds(event.target.value)}
                  className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                />
              </label>
              <label className="text-sm font-semibold text-slate-700">
                Samples per reading
                <input
                  type="number"
                  min={WATER_DISTANCE_SAMPLES_MIN}
                  max={WATER_DISTANCE_SAMPLES_MAX}
                  step="1"
                  value={distanceSamples}
                  onChange={(event) => setDistanceSamples(event.target.value)}
                  className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                />
              </label>
            </div>
          )}
            </div>
          </details>
          {cadenceWarning && (
            <p role="status" className="text-xs text-amber-700">{cadenceWarning}</p>
          )}
          <div>
            <span className="text-xs font-bold uppercase tracking-wide text-slate-500">
              Stress profile
            </span>
            <div className="mt-1">
              <StressProfileManager
                profiles={profiles}
                steps={steps}
                onStepsChange={setSteps}
                selectedProfileId={profileId}
                onSelectProfile={setProfileId}
                onCreateProfile={onCreateProfile}
                onArchiveProfile={onArchiveProfile}
                waterEvents={waterEvents}
                onWaterEventsChange={setWaterEvents}
                pumpConfig={pumpConfig}
                waterEventRowErrors={waterValidation.rowErrors}
                startLevelCm={startLevelCm}
              />
            </div>
          </div>
          {waterEvents.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="tank-setup">
              <span className="text-xs font-bold uppercase tracking-wide text-slate-500">
                Tank setup for the water events
              </span>
              <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-3">
                <label className="text-sm font-semibold text-slate-700">
                  Starting level (cm)
                  <input
                    type="number"
                    step="0.1"
                    value={startLevelCm}
                    onChange={(event) => setStartLevelCm(event.target.value)}
                    placeholder="read it off the ruler"
                    aria-label="Starting water level (cm)"
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                  />
                </label>
                <label className="text-sm font-semibold text-slate-700">
                  Lowest allowed level (cm)
                  <input
                    type="number"
                    step="0.1"
                    value={tankMinCm}
                    onChange={(event) => setTankMinCm(event.target.value)}
                    aria-label="Lowest allowed water level (cm)"
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                  />
                </label>
                <label className="text-sm font-semibold text-slate-700">
                  Highest allowed level (cm)
                  <input
                    type="number"
                    step="0.1"
                    value={tankMaxCm}
                    onChange={(event) => setTankMaxCm(event.target.value)}
                    aria-label="Highest allowed water level (cm)"
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                  />
                </label>
              </div>
              <p className="mt-1 text-xs text-slate-500">
                The pump has no feedback, so the events are checked against these limits before the
                run can start: the level may never leave this range.
              </p>
              {waterValidation.setupError && (
                <p role="alert" className="mt-1 text-xs text-red-700" data-testid="tank-setup-error">
                  {waterValidation.setupError}
                </p>
              )}
            </div>
          )}
          <p className="text-xs text-slate-500">
            Each step runs in order (hold at a fixed target, or ramp linearly toward it) before
            advancing to the next. Bounds/safety validation happens on the controller itself before
            it commits to the profile.
          </p>
          <button
            disabled={disabled || submitting}
            className="self-start rounded-xl bg-blue-700 px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50"
          >
            {submitting ? "Sending…" : "Start run"}
          </button>
        </form>
      )}
      {!activeRun && message && (
        <p role="status" className="mt-2 text-sm text-slate-700">{message}</p>
      )}
      <StartRunConfirmModal
        isOpen={confirmPayload !== null}
        onClose={() => setConfirmPayload(null)}
        payload={confirmPayload}
        pumpConfig={pumpConfig}
        onConfirm={confirmStart}
        submitting={submitting}
      />
    </div>
  );
}
